// Savestate serialization for the vendored gameboy-emulator (DMG).
//
// Implemented at adapter level instead of in vendor: the upstream
// core has no serialization API (see PATCHES.md for the surveyed
// surface). All machine state IS reachable through public and
// private fields on the Gameboy subcomponents, so this module
// captures them into a structuredClone-able blob and restores by
// writing back IN-PLACE wherever a downstream object holds a
// reference (CPU's DataView, GPU's vram/oam, Memory's memoryBytes,
// cartridge ramBytes) — replacing those references would orphan
// the consumers that captured them at construction time.
//
// The blob produced here is the `data` half of the CoreSaveState
// envelope (GbCoreBase wraps it with `core: "gb-roblouie"`); it stays
// opaque to callers. It also carries its own __format version string
// ("gb-roblouie/v1") which applyGbState re-checks — a second line of
// defense under the envelope's SaveStateMismatchError, and the marker
// coerceLegacySaveState uses to route pre-envelope GB blobs back to
// this core.
//
// Determinism: { save → run N → save A } and { save → load → run N
// → save B } must produce byte-equal A and B. The gb-smoke test
// asserts this.
const FORMAT_TAG = "gb-roblouie/v1";
export function captureGbState(gb) {
    const anyGb = gb;
    return {
        __format: FORMAT_TAG,
        cpu: captureCpu(anyGb.cpu),
        bus: captureBus(anyGb.bus),
        gpu: captureGpu(anyGb.gpu),
        apu: captureApu(anyGb.apu),
        interruptController: anyGb.interruptController.value & 0xff,
        timer: captureTimer(anyGb.timerController),
        input: captureInput(anyGb.input),
        cartridge: captureCartridge(anyGb.bus.cartridge),
    };
}
export function applyGbState(gb, raw) {
    const state = raw;
    if (!state || state.__format !== FORMAT_TAG) {
        throw new Error(`GbCore.loadState: state blob is not ${FORMAT_TAG} ` +
            `(got __format=${state?.__format ?? "<missing>"}). ` +
            `Savestates are core-private and version-specific.`);
    }
    const anyGb = gb;
    applyCpu(anyGb.cpu, state.cpu);
    applyBus(anyGb.bus, state.bus);
    applyGpu(anyGb.gpu, state.gpu);
    applyApu(anyGb.apu, state.apu);
    anyGb.interruptController.value = state.interruptController & 0xff;
    applyTimer(anyGb.timerController, state.timer);
    applyInput(anyGb.input, state.input);
    applyCartridge(anyGb.bus.cartridge, state.cartridge);
}
// ----- CPU -----
function captureCpu(cpu) {
    // registers all share a single 12-byte ArrayBuffer behind a DataView.
    // Reach the bytes via any one register's dataView (they're the same).
    const view = cpu.registers.programCounter.dataView;
    return {
        registers: copyBytes(new Uint8Array(view.buffer)),
        isInterruptMasterEnable: !!cpu.isInterruptMasterEnable,
        isHalted: !!cpu.isHalted,
        isStopped: !!cpu.isStopped,
    };
}
function applyCpu(cpu, s) {
    const view = cpu.registers.programCounter.dataView;
    const dst = new Uint8Array(view.buffer);
    dst.set(asBytes(s.registers));
    cpu.isInterruptMasterEnable = !!s.isInterruptMasterEnable;
    cpu.isHalted = !!s.isHalted;
    cpu.isStopped = !!s.isStopped;
}
// ----- bus / memory -----
function captureBus(bus) {
    return {
        memory: copyBytes(bus.memoryBytes),
        lastDmaValue: bus.lastDmaValue & 0xff,
    };
}
function applyBus(bus, s) {
    bus.memoryBytes.set(asBytes(s.memory));
    bus.lastDmaValue = s.lastDmaValue & 0xff;
}
// ----- GPU -----
function captureGpu(gpu) {
    return {
        vram: copyBytes(gpu.vram),
        oam: copyBytes(gpu.oam),
        cycleCounter: gpu.cycleCounter | 0,
        windowLinesDrawn: gpu.windowLinesDrawn | 0,
        lineY: gpu.lineY.value & 0xff,
        lineYCompare: gpu.lineYCompare.value & 0xff,
        lcdControl: gpu.lcdControl.value & 0xff,
        lcdStatus: gpu.lcdStatus.value & 0xff,
        scrollX: gpu.scrollX.value & 0xff,
        scrollY: gpu.scrollY.value & 0xff,
        windowX: gpu.windowX.value & 0xff,
        windowY: gpu.windowY.value & 0xff,
        backgroundPalette: gpu.backgroundPalette.value & 0xff,
        objectPalette0: gpu.objectPalettes[0].value & 0xff,
        objectPalette1: gpu.objectPalettes[1].value & 0xff,
    };
}
function applyGpu(gpu, s) {
    gpu.vram.set(asBytes(s.vram));
    gpu.oam.set(asBytes(s.oam));
    gpu.cycleCounter = s.cycleCounter | 0;
    gpu.windowLinesDrawn = s.windowLinesDrawn | 0;
    gpu.lineY.value = s.lineY & 0xff;
    gpu.lineYCompare.value = s.lineYCompare & 0xff;
    gpu.lcdControl.value = s.lcdControl & 0xff;
    gpu.lcdStatus.value = s.lcdStatus & 0xff;
    gpu.scrollX.value = s.scrollX & 0xff;
    gpu.scrollY.value = s.scrollY & 0xff;
    gpu.windowX.value = s.windowX & 0xff;
    gpu.windowY.value = s.windowY & 0xff;
    gpu.backgroundPalette.value = s.backgroundPalette & 0xff;
    gpu.objectPalettes[0].value = s.objectPalette0 & 0xff;
    gpu.objectPalettes[1].value = s.objectPalette1 & 0xff;
}
// ----- APU -----
function captureApu(apu) {
    return {
        isAudioEnabled: !!apu._isAudioEnabled,
        sampleRate: apu.sampleRate | 0,
        cyclesPerSample: Number(apu.cyclesPerSample),
        frameSequencerCycleCounter: Number(apu.frameSequencerCycleCounter),
        frameSequencerStep: apu.frameSequencerStep | 0,
        sampleCycleCounter: Number(apu.sampleCycleCounter),
        tempBuffer: new Float32Array(apu.tempBuffer),
        tempIndex: apu.tempIndex | 0,
        nr50: apu.nr50MasterVolume.value & 0xff,
        nr51: apu.nr51SoundPanning.value & 0xff,
        nr52: apu.nr52SoundEndFlag.value & 0xff,
        sound1: captureSound1(apu.sound1),
        sound2: captureSound2(apu.sound2),
        sound3: captureSound3(apu.sound3),
        sound4: captureSound4(apu.sound4),
    };
}
function applyApu(apu, s) {
    apu._isAudioEnabled = !!s.isAudioEnabled;
    apu.sampleRate = s.sampleRate | 0;
    apu.cyclesPerSample = Number(s.cyclesPerSample);
    apu.frameSequencerCycleCounter = Number(s.frameSequencerCycleCounter);
    apu.frameSequencerStep = s.frameSequencerStep | 0;
    apu.sampleCycleCounter = Number(s.sampleCycleCounter);
    // tempBuffer length is fixed (1024); copy in place if shape matches,
    // otherwise replace. Coerce first — JSON round-trips it as a numeric-
    // keyed object (see asFloats / asBytes).
    const temp = asFloats(s.tempBuffer);
    if (apu.tempBuffer.length === temp.length) {
        apu.tempBuffer.set(temp);
    }
    else {
        apu.tempBuffer = temp;
    }
    apu.tempIndex = s.tempIndex | 0;
    apu.nr50MasterVolume.value = (s.nr50 ?? 0) & 0xff;
    apu.nr51SoundPanning.value = (s.nr51 ?? 0) & 0xff;
    apu.nr52SoundEndFlag.value = s.nr52 & 0xff;
    applySound1(apu.sound1, s.sound1);
    applySound2(apu.sound2, s.sound2);
    applySound3(apu.sound3, s.sound3);
    applySound4(apu.sound4, s.sound4);
}
function captureSound1(s) {
    return {
        positionInDutyCycle: s.positionInDutyCycle | 0,
        frequencyTimer: s.frequencyTimer | 0,
        frequencyPeriod: s.frequencyPeriod | 0,
        lengthTimer: s.lengthTimer | 0,
        volume: s.volume | 0,
        envelopePeriodTimer: s.enveloper.envelopePeriodTimer | 0,
        isSweepEnabled: !!s.isSweepEnabled,
        shadowFrequency: s.shadowFrequency | 0,
        sweepTimer: s.sweepTimer | 0,
        isActive: !!s.isActive,
        nr10: s.nr10SweepControl.value & 0xff,
        nr11: s.nr11LengthAndDutyCycle.value & 0xff,
        nr12: s.nr12EnvelopeControl.value & 0xff,
        nr13: s.nr13LowOrderFrequency.value & 0xff,
        nr14: s.nr14HighOrderFrequency.value & 0xff,
    };
}
function applySound1(s, st) {
    s.positionInDutyCycle = st.positionInDutyCycle | 0;
    s.frequencyTimer = st.frequencyTimer | 0;
    s.frequencyPeriod = st.frequencyPeriod | 0;
    s.lengthTimer = st.lengthTimer | 0;
    s.volume = st.volume | 0;
    s.enveloper.envelopePeriodTimer = st.envelopePeriodTimer | 0;
    s.isSweepEnabled = !!st.isSweepEnabled;
    s.shadowFrequency = st.shadowFrequency | 0;
    s.sweepTimer = st.sweepTimer | 0;
    s.isActive = !!st.isActive;
    s.nr10SweepControl.value = st.nr10 & 0xff;
    s.nr11LengthAndDutyCycle.value = st.nr11 & 0xff;
    s.nr12EnvelopeControl.value = st.nr12 & 0xff;
    s.nr13LowOrderFrequency.value = st.nr13 & 0xff;
    s.nr14HighOrderFrequency.value = st.nr14 & 0xff;
}
function captureSound2(s) {
    return {
        positionInDutyCycle: s.positionInDutyCycle | 0,
        frequencyTimer: s.frequencyTimer | 0,
        frequencyPeriod: s.frequencyPeriod | 0,
        lengthTimer: s.lengthTimer | 0,
        volume: s.volume | 0,
        envelopePeriodTimer: s.enveloper.envelopePeriodTimer | 0,
        isActive: !!s.isActive,
        nr21: s.nr21LengthAndDutyCycle.value & 0xff,
        nr22: s.nr22EnvelopeControl.value & 0xff,
        nr23: s.nr23LowOrderFrequency.value & 0xff,
        nr24: s.nr24HighOrderFrequency.value & 0xff,
    };
}
function applySound2(s, st) {
    s.positionInDutyCycle = st.positionInDutyCycle | 0;
    s.frequencyTimer = st.frequencyTimer | 0;
    s.frequencyPeriod = st.frequencyPeriod | 0;
    s.lengthTimer = st.lengthTimer | 0;
    s.volume = st.volume | 0;
    s.enveloper.envelopePeriodTimer = st.envelopePeriodTimer | 0;
    s.isActive = !!st.isActive;
    s.nr21LengthAndDutyCycle.value = st.nr21 & 0xff;
    s.nr22EnvelopeControl.value = st.nr22 & 0xff;
    s.nr23LowOrderFrequency.value = st.nr23 & 0xff;
    s.nr24HighOrderFrequency.value = st.nr24 & 0xff;
}
function captureSound3(s) {
    return {
        frequencyTimer: s.frequencyTimer | 0,
        frequencyPeriod: s.frequencyPeriod | 0,
        lengthTimer: s.lengthTimer | 0,
        waveTablePosition: s.waveTablePosition | 0,
        waveformRam: copyBytes(s.waveformRam),
        isActive: !!s.isActive,
        nr30: s.nr30SoundOff.value & 0xff,
        nr31: s.nr31Length.value & 0xff,
        nr32: s.nr32OutputLevel.value & 0xff,
        nr33: s.nr33LowOrderFrequency.value & 0xff,
        nr34: s.nr34HighOrderFrequency.value & 0xff,
    };
}
function applySound3(s, st) {
    s.frequencyTimer = st.frequencyTimer | 0;
    s.frequencyPeriod = st.frequencyPeriod | 0;
    s.lengthTimer = st.lengthTimer | 0;
    s.waveTablePosition = st.waveTablePosition | 0;
    s.waveformRam.set(asBytes(st.waveformRam));
    s.isActive = !!st.isActive;
    s.nr30SoundOff.value = st.nr30 & 0xff;
    s.nr31Length.value = st.nr31 & 0xff;
    s.nr32OutputLevel.value = st.nr32 & 0xff;
    s.nr33LowOrderFrequency.value = st.nr33 & 0xff;
    s.nr34HighOrderFrequency.value = st.nr34 & 0xff;
}
function captureSound4(s) {
    return {
        frequencyTimer: s.frequencyTimer | 0,
        frequencyPeriod: s.frequencyPeriod | 0,
        lengthTimer: s.lengthTimer | 0,
        volume: s.volume | 0,
        envelopePeriodTimer: s.enveloper.envelopePeriodTimer | 0,
        linearFeedbackShift: s.linearFeedbackShift | 0,
        isActive: !!s.isActive,
        nr41: s.nr41Length.value & 0xff,
        nr42: s.nr42EnvelopeControl.value & 0xff,
        nr43: s.nr43Polynomial.value & 0xff,
        nr44: s.nr44ContinuousSelection.value & 0xff,
    };
}
function applySound4(s, st) {
    s.frequencyTimer = st.frequencyTimer | 0;
    s.frequencyPeriod = st.frequencyPeriod | 0;
    s.lengthTimer = st.lengthTimer | 0;
    s.volume = st.volume | 0;
    s.enveloper.envelopePeriodTimer = st.envelopePeriodTimer | 0;
    s.linearFeedbackShift = st.linearFeedbackShift | 0;
    s.isActive = !!st.isActive;
    s.nr41Length.value = st.nr41 & 0xff;
    s.nr42EnvelopeControl.value = st.nr42 & 0xff;
    s.nr43Polynomial.value = st.nr43 & 0xff;
    s.nr44ContinuousSelection.value = st.nr44 & 0xff;
}
// ----- timer -----
function captureTimer(t) {
    return {
        div: t.div & 0xffff,
        tima: t.tima.value & 0xff,
        tma: t.tma.value & 0xff,
        tac: t.tac.value & 0xff,
        isReloadPending: !!t.isReloadPending,
        reloadDelay: t.reloadDelay | 0,
    };
}
function applyTimer(t, s) {
    t.div = s.div & 0xffff;
    t.tima.value = s.tima & 0xff;
    t.tma.value = s.tma & 0xff;
    t.tac.value = s.tac & 0xff;
    t.isReloadPending = !!s.isReloadPending;
    t.reloadDelay = s.reloadDelay | 0;
}
// ----- input -----
//
// gb.input is a per-instance Input owned by the Gameboy (MultiNostalgia
// patch — the vendor originally exported one shared module singleton).
// captureInput/applyInput read and write `anyGb.input`, so they touch
// only this instance's controller: loadState on instance B leaves
// instance A's input untouched, and two GBs in lockstep (a future GB
// fidelity rig) each restore their own. This matches the NES core,
// whose input already lives on the instance.
function captureInput(input) {
    return {
        up: !!input.isPressingUp,
        down: !!input.isPressingDown,
        left: !!input.isPressingLeft,
        right: !!input.isPressingRight,
        select: !!input.isPressingSelect,
        start: !!input.isPressingStart,
        a: !!input.isPressingA,
        b: !!input.isPressingB,
        isPollingDirections: !!input.isPollingDirections,
        isPollingButtons: !!input.isPollingButtons,
    };
}
function applyInput(input, s) {
    input.isPressingUp = !!s.up;
    input.isPressingDown = !!s.down;
    input.isPressingLeft = !!s.left;
    input.isPressingRight = !!s.right;
    input.isPressingSelect = !!s.select;
    input.isPressingStart = !!s.start;
    input.isPressingA = !!s.a;
    input.isPressingB = !!s.b;
    input.isPollingDirections = !!s.isPollingDirections;
    input.isPollingButtons = !!s.isPollingButtons;
}
// ----- cartridge -----
function captureCartridge(c) {
    if (!c)
        return { kind: "none" };
    // Detect by feature, not constructor name (resilient to bundler
    // renaming). Mbc1 has bank1/bank2/mode; Mbc3 has romBank +
    // ramBankOrRtcSelection; the plain Cartridge base has neither and
    // its writeByte is a no-op (ROM-only carts).
    if ("bank1" in c && "bank2" in c && "mode" in c) {
        return {
            kind: "mbc1",
            isRamEnabled: !!c.isRamEnabled,
            bank1: c.bank1 | 0,
            bank2: c.bank2 | 0,
            mode: c.mode | 0,
            ram: copyBytes(c.ramBytes),
        };
    }
    if ("romBank" in c && "ramBankOrRtcSelection" in c) {
        return {
            kind: "mbc3",
            isRamAndRtcEnabled: !!c.isRamAndRtcEnabled,
            romBank: c.romBank | 0,
            ramBankOrRtcSelection: c.ramBankOrRtcSelection | 0,
            ram: copyBytes(c.ramBytes),
        };
    }
    return { kind: "rom-only" };
}
function applyCartridge(c, s) {
    if (s.kind === "none" || s.kind === "rom-only")
        return;
    if (s.kind === "mbc1") {
        if (!("bank1" in c)) {
            throw new Error("loadState: state is MBC1 but cartridge is not");
        }
        c.isRamEnabled = !!s.isRamEnabled;
        c.bank1 = s.bank1 | 0;
        c.bank2 = s.bank2 | 0;
        c.mode = s.mode | 0;
        // ramBytes is a Uint8Array view onto ramData (ArrayBuffer); copying
        // in-place keeps the view (and ramDataView) valid.
        c.ramBytes.set(asBytes(s.ram));
        return;
    }
    if (s.kind === "mbc3") {
        if (!("romBank" in c)) {
            throw new Error("loadState: state is MBC3 but cartridge is not");
        }
        c.isRamAndRtcEnabled = !!s.isRamAndRtcEnabled;
        c.romBank = s.romBank | 0;
        c.ramBankOrRtcSelection = s.ramBankOrRtcSelection | 0;
        c.ramBytes.set(asBytes(s.ram));
        return;
    }
}
// ----- utils -----
function copyBytes(src) {
    const out = new Uint8Array(src.length);
    out.set(src);
    return out;
}
// Coerce a deserialized byte buffer back into a Uint8Array. The savestate
// `data` blob travels two ways: through structuredClone (worker mode), which
// preserves typed arrays, and through JSON (the `.munos-tas` recording file),
// which does NOT — `JSON.stringify(new Uint8Array([1,2]))` is `{"0":1,"1":2}`,
// a plain object with numeric keys and no `length`. Feeding that straight to
// `Uint8Array.prototype.set` copies ZERO bytes (set reads the missing
// `length` as 0), so a TAS-loaded savestate would restore all-zero CPU/VRAM/
// OAM/RAM and the core boots dead. This accepts the typed-array, plain-array,
// and numeric-keyed-object forms uniformly.
function asBytes(v) {
    if (v instanceof Uint8Array)
        return v;
    if (Array.isArray(v))
        return Uint8Array.from(v);
    // JSON-deserialized typed array: { "0": b, "1": b, ... }.
    const keys = Object.keys(v);
    const out = new Uint8Array(keys.length);
    for (let i = 0; i < out.length; i++)
        out[i] = (v[i] ?? 0) & 0xff;
    return out;
}
// Float32 counterpart of asBytes for the APU's sample scratch buffer.
function asFloats(v) {
    if (v instanceof Float32Array)
        return v;
    if (Array.isArray(v))
        return Float32Array.from(v);
    const keys = Object.keys(v);
    const out = new Float32Array(keys.length);
    for (let i = 0; i < out.length; i++)
        out[i] = Number(v[i] ?? 0);
    return out;
}
//# sourceMappingURL=savestate.js.map